import { PaletteMode } from "@mui/material";
import { Color } from "@mui/material/styles";

interface CustomColor extends Color {
  main: string,
  light: string,
  dark: string,
  contrastText: string,
}

// Parse hex color to RGB components
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

// Convert RGB to hex
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => {
    const hex = Math.round(Math.max(0, Math.min(255, x))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

// Mix color with white (lighten) or black (darken)
function mixColor(hex: string, mixWith: 'white' | 'black', amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const target = mixWith === 'white' ? 255 : 0;
  return rgbToHex(
    r + (target - r) * amount,
    g + (target - g) * amount,
    b + (target - b) * amount
  );
}

// Helper to generate shade variants from a base color (all opaque)
export function generateShades(main: string) {
  return {
    50: mixColor(main, 'white', 0.92),
    100: mixColor(main, 'white', 0.84),
    200: mixColor(main, 'white', 0.76),
    300: mixColor(main, 'white', 0.62),
    400: mixColor(main, 'white', 0.46),
    500: main,
    600: mixColor(main, 'black', 0.13),
    700: mixColor(main, 'black', 0.30),
    800: mixColor(main, 'black', 0.46),
    900: mixColor(main, 'black', 0.62),
    main,
    light: mixColor(main, 'white', 0.3),
    dark: mixColor(main, 'black', 0.3),
    contrastText: '#ffffff',
    A100: mixColor(main, 'white', 0.76),
    A200: mixColor(main, 'white', 0.60),
    A400: mixColor(main, 'white', 0.38),
    A700: mixColor(main, 'black', 0.15),
  };
}

// Brand colors - maintained in both light and dark modes
const BRAND_PRIMARY = '#375934';
const BRAND_SECONDARY = '#6b8e65';

function createPalette(mode: PaletteMode) {
  const isDark = mode === 'dark';

  return {
    mode,
    primary: {
      ...generateShades(BRAND_PRIMARY),
      light: '#5a7d56',
      dark: '#263d24',
    },
    secondary: {
      ...generateShades(BRAND_SECONDARY),
      light: '#8fb088',
      dark: '#4a6345',
    },
    background: {
      default: isDark ? '#121212' : '#f5f5f5',
      paper: isDark ? '#1e1e1e' : '#ffffff',
    },
    text: {
      primary: isDark ? '#ffffff' : '#212121',
      secondary: isDark ? 'rgba(255, 255, 255, 0.7)' : '#757575',
    },
    error: {
      ...generateShades('#d32f2f'),
      light: '#ef5350',
      dark: '#c62828',
    },
    warning: {
      ...generateShades('#f57c00'),
      light: '#ff9800',
      dark: '#e65100',
    },
    info: {
      ...generateShades('#0288d1'),
      light: '#03a9f4',
      dark: '#01579b',
    },
    success: {
      ...generateShades('#388e3c'),
      light: '#4caf50',
      dark: '#1b5e20',
    },
    divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
  };
}

export { createPalette, BRAND_PRIMARY, BRAND_SECONDARY }
